import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Dan" },
  { to: "/four-levels", label: "Four Levels" },
  { to: "/pattern-reveal", label: "Pattern Reveal" },
  { to: "/weekly-quantum-edge", label: "Weekly Edge" },
  { to: "/forward-reset", label: "Forward Reset" },
  { to: "/daily-quantum-edge", label: "Daily Edge" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-40 backdrop-blur-md transition-colors duration-500 ${
        scrolled ? "bg-brand-cream/90 border-b border-brand-rule/70" : "bg-brand-cream/70"
      }`}
    >
      <div className="container-narrow flex items-center justify-between gap-6 h-[76px] sm:h-[84px]">
        <Link
          to="/"
          className="flex items-center group shrink-0"
          data-testid="brand-logo-link"
          aria-label="Quantum Results — home"
        >
          <Logo className="h-9 w-auto sm:h-11" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="desktop-nav">
          {NAV.slice(1, 8).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-[13.5px] tracking-wide transition-colors ${
                  isActive
                    ? "text-brand-blue"
                    : "text-brand-ink/70 hover:text-brand-ink"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <NavLink
            to="/contact"
            data-testid="nav-cta-contact"
            className="whitespace-nowrap text-[13.5px] px-4 py-2 rounded-full border border-brand-ink/25 text-brand-ink hover:border-brand-blue hover:text-brand-blue transition-colors"
          >
            Start a Conversation
          </NavLink>
        </nav>

        <button
          className="lg:hidden p-2 -mr-2 text-brand-ink"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-rule/70 bg-brand-cream" data-testid="mobile-nav">
          <div className="container-narrow py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 border-b border-brand-rule/50 last:border-0 text-[15px] ${
                    isActive ? "text-brand-blue" : "text-brand-ink/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
